import get from 'lodash/get';
import isObject from 'lodash/isObject';
import fromPairs from 'lodash/fromPairs';

/**
 * Get the raw query string from given query or current page query params.
 *
 * @param  {string} queryString The query string to parse
 * @param  {mixed} fallback     The fallback if the given query string is empty.
 *
 * @returns {mixed}              Query string or fallback.
 */
export const getRawQuery = (queryString, fallback = '?') => {
	const query = null !== queryString ? queryString : window.location.search;

	return query ? (0 === query.indexOf('?') ? query.substring(1) : query) : fallback;
};

/**
 * Parses a query string into an object
 *
 * @param {string} queryString The query string to parse
 *
 * @returns {Object|boolean} The parsed query with key/value
 */
export const parse = (queryString = null) => {
	const query = getRawQuery(queryString, false);
	if (!query) {
		return false;
	}

	const params = new URLSearchParams(query);

	return fromPairs(Array.from(params.entries()));
};

/**
 * Add or override a key/value to an existing query string
 *
 * @param {string} key The query string key to add
 * @param {string|Array} value The value to assign. If array, the value will be converted to a comma-separated value
 * @param {string} queryString The query string to add the new key/value
 *
 * @returns {string} The parsed query with key/value
 */
export const addArg = (key, value, queryString = null) => {
	// If the key is empty, return the query string, otherwise it will generate
	// an invalid query string.
	if (!key) {
		return queryString || '';
	}

	const params = new URLSearchParams(getRawQuery(queryString));
	params.set(key, value);

	return params.toString();
};

/**
 * Remove a key or key/value from an existing query string
 *
 * @param {string} key The query string key to remove
 * @param {string|Array} value The value to compare. If provided, will only remove
 *                             the key if the values match.
 * @param {string} queryString The query string to remove the key from.
 *
 * @returns {string} The parsed query with key/value
 */
export const removeArg = (key, value = null, queryString = null) => {
	// If the key is empty, return the query string, otherwise it will generate
	// an invalid query string.
	if (!key) {
		return queryString || '';
	}

	const params = new URLSearchParams(getRawQuery(queryString));

	if (value) {
		// If value provided, only delete if the value matches.
		if (String(value) === params.get(key)) {
			params.delete(key);
		}
	} else {
		params.delete(key);
	}

	return params.toString();
};

/**
 * Parses and retrieves a key's value from an existing query string
 *
 * @param {Object} key         The key retrieve from the query string.
 * @param {string} queryString The query string to parse.
 *
 * @returns {string} The passed key's value.
 */
export const getArg = (key, queryString = null) => {
	return get(parse(queryString), key);
};

/**
 * Serialize URL query params. Handles array/object args.
 *
 * @param {Object} params The export parameters
 * @param {string} prefix Option prefix
 *
 * @returns {string} The serialized query string
 */
export const serialize = (params, prefix) => {
	let query = isObject(params)
		? Object.keys(params).map((key) => {
				const value = null === params[key] || undefined === params[key] ? '' : params[key];

				if (params.constructor === Array) {
					key = `${prefix}[]`;
				} else if (params.constructor === Object) {
					key = prefix ? `${prefix}[${key}]` : key;
				}

				if (isObject(value)) {
					return serialize(value, key);
				}

				return `${key}=${encodeURIComponent(value)}`;
		  })
		: [];
	query = query.filter((k) => !!k);

	return query.length ? [].concat.apply([], query).join('&') : '';
};

export default {
	parse,
	add: addArg,
	get: getArg,
	remove: removeArg,
	serialize,
};
